/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { FilterBar } from '@/shared/ui/FilterBar'
import { DataTable } from '@/shared/ui/DataTable'
import { StatusBadge } from '@/shared/ui/Badge'
import { Card } from '@/shared/ui/Card'
import { StatCard } from '@/shared/ui/StatCard'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { Spinner } from '@/shared/ui/Spinner'
import { DollarSign, Wallet, History } from 'lucide-react'
import { formatCurrency, formatDate } from '@/shared/utils'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { useTrainerPayouts, useSchoolPayouts, usePayoutHistory, useTrainerBreakdown, usePaySessions } from '@/api/hooks'
import { useDebounce } from '@/shared/hooks'
import type { ColumnDef } from '@tanstack/react-table'

const PAYMENT_METHODS = ['BANK_TRANSFER', 'CASH', 'CARD', 'WALLET', 'EXTERNAL']

// v1 admin payout endpoints return per-recipient AGGREGATES with different field
// names per type. These helpers read whichever set is present.
const num = (v: unknown) => (typeof v === 'number' && !isNaN(v) ? v : Number(v) || 0)
const recipientId   = (r: any) => r.trainer_id ?? r.vendor_id ?? r.id
const recipientName = (r: any) => r.trainer_name ?? r.vendor_name ?? '—'
const recipientPhone= (r: any) => r.trainer_phone ?? r.vendor_phone ?? ''
const earned  = (r: any) => num(r.total_payout ?? r.total_earning)
const paidAmt = (r: any) => num(r.transferred_amount)
const balance = (r: any) => (r.balance != null ? num(r.balance) : earned(r) - paidAmt(r))
const bookings= (r: any) => num(r.paid_bookings ?? r.total_bookings ?? r.completed_bookings ?? r.total_paid_and_completed_booking)

export function PayoutListPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const isSchool = location.pathname.startsWith('/school')
  const type = isSchool ? 'school' : 'trainer'
  const tabs = isSchool
    ? [{ labelKey:'tabs.school', path:'/school/school-payouts' }]
    : [{ labelKey:'tabs.trainer', path:'/dashboard/payouts/trainer' }]

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)
  const [statusFilter, setStatusFilter] = useState<'owed' | 'paid' | 'zero' | 'all'>('owed')
  const [page, setPage] = useState(1)
  const PER_PAGE = 15

  // Fetch the full set so the status filter can hide zero-earned trainers — the
  // default id-desc order otherwise surfaces only newest (0-earning) trainers.
  const trainerQ = useTrainerPayouts(type==='trainer' ? { page:1, limit:1000, search: debouncedSearch || undefined } : undefined)
  const schoolQ  = useSchoolPayouts(type==='school' ? { page:1, limit:100 } : undefined)

  const q = type === 'trainer' ? trainerQ : schoolQ

  const rows: any[] = useMemo(() => (Array.isArray(q.data?.data) ? q.data!.data : []), [q.data])

  // Classify each trainer by settlement status.
  const statusOf = (r: any): 'owed' | 'paid' | 'zero' => {
    if (earned(r) <= 0.005) return 'zero'
    return balance(r) > 0.005 ? 'owed' : 'paid'
  }
  const counts = useMemo(() => {
    const c = { owed: 0, paid: 0, zero: 0, all: rows.length }
    rows.forEach(r => { c[statusOf(r)]++ })
    return c
  }, [rows])
  const filteredRows = useMemo(
    () => (statusFilter === 'all' ? rows : rows.filter(r => statusOf(r) === statusFilter)),
    [rows, statusFilter],
  )

  // Client-side pagination over the filtered set.
  const total = filteredRows.length
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE))
  const pageRows = useMemo(() => filteredRows.slice((page - 1) * PER_PAGE, page * PER_PAGE), [filteredRows, page])

  // Stats reflect the current section.
  const totalEarned = useMemo(() => filteredRows.reduce((s, r) => s + earned(r), 0), [filteredRows])
  const totalPaid   = useMemo(() => filteredRows.reduce((s, r) => s + paidAmt(r), 0), [filteredRows])

  // Reset to page 1 whenever the filter or search changes.
  const resetPage = () => setPage(1)

  // ── History modal (trainer only) — payments + every paid session ──────────
  const [historyTarget, setHistoryTarget] = useState<any | null>(null)
  const historyQ = usePayoutHistory(historyTarget ? { trainer_id: recipientId(historyTarget), page: 1, limit: 50 } : undefined)
  const historyRows: any[] = useMemo(() => (Array.isArray(historyQ.data?.data) ? historyQ.data!.data : []), [historyQ.data])
  // Reuse the breakdown to list which individual sessions have been paid.
  const historyBreakdownQ = useTrainerBreakdown(historyTarget ? recipientId(historyTarget) : undefined)
  const paidSessions = useMemo(() => {
    const out: any[] = []
    const bs = Array.isArray(historyBreakdownQ.data?.bookings) ? historyBreakdownQ.data!.bookings : []
    bs.forEach((b: any) => (b.sessions ?? []).forEach((s: any) => {
      if (s.paid) out.push({ ...s, booking_id: b.booking_id, package_name: b.package_name })
    }))
    return out.sort((a, b) => String(b.paid_at ?? '').localeCompare(String(a.paid_at ?? '')))
  }, [historyBreakdownQ.data])

  // ── Sessions breakdown + per-session pay (trainer only) ───────────────────
  // `selected` = which session lines are ticked; `amounts` = the editable
  // amount to pay for each (defaults to that session's remaining, editable down
  // for a partial payment — the rest stays owed).
  const [breakdownTarget, setBreakdownTarget] = useState<any | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [amounts, setAmounts] = useState<Record<number, string>>({})
  const [sessMethod, setSessMethod] = useState(PAYMENT_METHODS[0])
  const [sessTxn, setSessTxn] = useState('')
  const [sessRemarks, setSessRemarks] = useState('')
  const paySessions = usePaySessions()
  const breakdownQ = useTrainerBreakdown(breakdownTarget ? recipientId(breakdownTarget) : undefined)
  const breakdownBookings: any[] = useMemo(() => (Array.isArray(breakdownQ.data?.bookings) ? breakdownQ.data!.bookings : []), [breakdownQ.data])

  const openBreakdown = (trainer: any) => {
    setBreakdownTarget(trainer)
    setSelected(new Set()); setAmounts({}); setSessMethod(PAYMENT_METHODS[0]); setSessTxn(''); setSessRemarks('')
  }
  const toggleSession = (s: any) => {
    const id = s.transaction_id
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
    setAmounts(prev => (prev[id] !== undefined ? prev : { ...prev, [id]: String(num(s.remaining)) }))
  }
  const toggleBooking = (b: any, on: boolean) => {
    setSelected(prev => {
      const n = new Set(prev)
      ;(b.sessions ?? []).forEach((s: any) => { if (num(s.remaining) > 0) { on ? n.add(s.transaction_id) : n.delete(s.transaction_id) } })
      return n
    })
    if (on) setAmounts(prev => {
      const next = { ...prev }
      ;(b.sessions ?? []).forEach((s: any) => { if (num(s.remaining) > 0 && next[s.transaction_id] === undefined) next[s.transaction_id] = String(num(s.remaining)) })
      return next
    })
  }
  const setAmount = (id: number, v: string) => setAmounts(prev => ({ ...prev, [id]: v }))
  const selectedTotal = useMemo(() => {
    let sum = 0
    selected.forEach(id => { sum += Number(amounts[id]) || 0 })
    return sum
  }, [selected, amounts])

  const submitSessionPay = () => {
    if (!breakdownTarget || selected.size === 0) { toast.error(t('payoutList.breakdown.selectFirst')); return }
    const items = Array.from(selected).map(id => ({ transaction_id: id, amount: Number(amounts[id]) || 0 })).filter(it => it.amount > 0)
    if (items.length === 0) { toast.error(t('payoutList.breakdown.enterAmount')); return }
    paySessions.mutate(
      {
        trainer_id: recipientId(breakdownTarget),
        items,
        payment_method: sessMethod,
        txn_id: sessTxn.trim() || undefined,
        remarks: sessRemarks.trim() || undefined,
      },
      {
        onSuccess: (d: any) => { toast.success(t('payoutList.breakdown.paid', { count: d?.sessions_paid ?? items.length, amount: formatCurrency(num(d?.amount)) })); setSelected(new Set()); setAmounts({}); setSessTxn(''); setSessRemarks('') },
        onError: (e) => toast.error(getApiError(e)),
      },
    )
  }

  const columns: ColumnDef<any>[] = [
    { header:t('payoutList.columns.id'), id:'id',
      cell:({row}) => <span className="font-mono text-xs text-gray-400">#{recipientId(row.original)}</span> },
    { header:t('payoutList.columns.recipient'), id:'recipient', cell:({row}) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{recipientName(row.original)}</p>
          {recipientPhone(row.original) && <p className="text-xs text-gray-400">{recipientPhone(row.original)}</p>}
        </div>
    )},
    { header:t('payoutList.columns.earned'), id:'earned',
      cell:({row}) => <span className="font-semibold">{formatCurrency(earned(row.original))}</span> },
    { header:t('payoutList.columns.paid'), id:'paid',
      cell:({row}) => <span className="text-sm text-green-600">{formatCurrency(paidAmt(row.original))}</span> },
    { header:t('payoutList.columns.balance'), id:'balance',
      cell:({row}) => <span className="font-bold">{formatCurrency(balance(row.original))}</span> },
    { header:t('payoutList.columns.bookings'), id:'bookings',
      cell:({row}) => <span className="text-sm text-gray-500">{bookings(row.original)}</span> },
  ]

  if (type === 'trainer') {
    columns.push({
      header: t('payoutList.columns.actions'), id: 'actions',
      cell: ({ row }) => {
        const bal = balance(row.original)
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openBreakdown(row.original)}
              disabled={bal <= 0}
              className="flex items-center gap-1.5 px-3 py-1 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Wallet size={12}/> {t('payoutList.pay.button')}
            </button>
            <button
              onClick={() => setHistoryTarget(row.original)}
              className="flex items-center gap-1.5 px-3 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              <History size={12}/> {t('payoutList.history.button')}
            </button>
          </div>
        )
      },
    })
  }

  const notImplemented = type === 'school' && !q.isLoading && rows.length === 0 && !q.isError

  return (
    <div className="space-y-5">
      <PageHeader title={t('payoutList.title')} breadcrumbs={[{label:t('payoutList.breadcrumb.dashboard'),to:isSchool?'/school/overview':'/dashboard'},{label:t('payoutList.breadcrumb.payouts')}]}/>
      {tabs.length > 1 && (
        <div className="flex border-b border-gray-100 bg-white rounded-t-xl overflow-x-auto">
          {tabs.map(tab => (
            <NavLink key={tab.path} to={tab.path}
              className={({isActive}) => `px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive?'border-brand-500 text-brand-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t(`payoutList.${tab.labelKey}`)}
            </NavLink>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <StatCard title={t('payoutList.stats.totalEarned')} value={formatCurrency(totalEarned)} icon={<DollarSign size={18}/>} color="blue"/>
        <StatCard title={t('payoutList.stats.totalPaid')}   value={formatCurrency(totalPaid)}   icon={<DollarSign size={18}/>} color="green"/>
      </div>
      <Card padding={false}>
        {type === 'trainer' && (
          <>
            <div className="flex flex-wrap gap-2 px-5 pt-5">
              {([
                { key: 'owed', label: t('payoutList.sections.owed') },
                { key: 'paid', label: t('payoutList.sections.paid') },
                { key: 'zero', label: t('payoutList.sections.zero') },
                { key: 'all',  label: t('payoutList.sections.all') },
              ] as const).map(s => (
                <button key={s.key}
                  onClick={() => { setStatusFilter(s.key); resetPage() }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${statusFilter === s.key ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  {s.label} <span className={statusFilter === s.key ? 'text-white/80' : 'text-gray-400'}>({counts[s.key]})</span>
                </button>
              ))}
            </div>
            <div className="p-5 border-b border-gray-100">
              <FilterBar search={{ value: search, onChange: (v) => { setSearch(v); resetPage() }, placeholder: t('payoutList.searchPlaceholder') }} />
            </div>
          </>
        )}
        {q.isError && <p className="px-5 py-4 text-sm text-red-500">{t('payoutList.error')}</p>}
        {notImplemented && <p className="px-5 py-4 text-sm text-gray-500">{t('payoutList.notImplemented')}</p>}
        <DataTable<any>
          columns={columns} data={pageRows} isLoading={q.isLoading} emptyMessage={t('payoutList.empty')}
          pagination={{ page, per_page: PER_PAGE, total, last_page: lastPage }}
          onPaginationChange={(p) => setPage(p.page)}
        />
      </Card>


      {/* History modal */}
      <Modal open={!!historyTarget} onClose={() => setHistoryTarget(null)} title={t('payoutList.history.title', { name: historyTarget ? recipientName(historyTarget) : '' })} size="xl">
        {historyTarget && (
          <div className="space-y-6">
            {/* Paid sessions — every session that has been settled */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900">{t('payoutList.history.paidSessions')}</h3>
              {historyBreakdownQ.isLoading && <div className="flex justify-center py-6"><Spinner/></div>}
              {!historyBreakdownQ.isLoading && paidSessions.length === 0 && (
                <p className="text-sm text-gray-500 py-4">{t('payoutList.history.noPaidSessions')}</p>
              )}
              {paidSessions.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="py-2 pe-3">{t('payoutList.history.paidOn')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.booking')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.sessionDate')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.payoutRef')}</th>
                        <th className="py-2 text-right">{t('payoutList.history.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidSessions.map((s) => (
                        <tr key={s.transaction_id} className="border-b border-gray-50">
                          <td className="py-2 pe-3 text-gray-500 whitespace-nowrap">{formatDate(s.paid_at)}</td>
                          <td className="py-2 pe-3 text-gray-600 truncate max-w-[160px]">{s.package_name || t('payoutList.breakdown.booking')} <span className="font-mono text-xs text-gray-400">#{s.booking_id}</span></td>
                          <td className="py-2 pe-3 text-gray-500 whitespace-nowrap">{formatDate(s.date)}</td>
                          <td className="py-2 pe-3 text-gray-400 font-mono text-xs">#{s.payout_id}</td>
                          <td className="py-2 text-right font-semibold">{formatCurrency(num(s.amount))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payments — the transfers recorded to the trainer */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900">{t('payoutList.history.payments')}</h3>
              {historyQ.isLoading && <div className="flex justify-center py-6"><Spinner/></div>}
              {historyQ.isError && <p className="text-sm text-red-500">{t('payoutList.history.error')}</p>}
              {!historyQ.isLoading && !historyQ.isError && historyRows.length === 0 && (
                <p className="text-sm text-gray-500 py-4">{t('payoutList.history.empty')}</p>
              )}
              {historyRows.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="py-2 pe-3">{t('payoutList.history.date')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.amount')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.method')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.status')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.txnId')}</th>
                        <th className="py-2 pe-3">{t('payoutList.history.by')}</th>
                        <th className="py-2">{t('payoutList.history.note')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyRows.map((h) => (
                        <tr key={h.id} className="border-b border-gray-50">
                          <td className="py-2 pe-3 text-gray-500 whitespace-nowrap">{formatDate(h.transferred_at ?? h.created_at)}</td>
                          <td className="py-2 pe-3 font-semibold whitespace-nowrap">{formatCurrency(num(h.amount))}</td>
                          <td className="py-2 pe-3 text-gray-500">{h.payment_method || '—'}</td>
                          <td className="py-2 pe-3">{h.status ? <StatusBadge status={String(h.status)}/> : '—'}</td>
                          <td className="py-2 pe-3 text-gray-500">{h.txn_id || '—'}</td>
                          <td className="py-2 pe-3 text-gray-500">{h.admin_name || '—'}</td>
                          <td className="py-2 text-gray-500">{h.note || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Sessions breakdown modal — select sessions & pay per-session */}
      <Modal open={!!breakdownTarget} onClose={() => setBreakdownTarget(null)} title={t('payoutList.breakdown.title', { name: breakdownTarget ? recipientName(breakdownTarget) : '' })} size="xl">
        {breakdownTarget && (
          <div className="space-y-4">
            {breakdownQ.isLoading && <div className="flex justify-center py-8"><Spinner/></div>}
            {breakdownQ.isError && <p className="text-sm text-red-500">{t('payoutList.breakdown.error')}</p>}
            {!breakdownQ.isLoading && !breakdownQ.isError && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-500">{t('payoutList.breakdown.grandTotal')}</span>
                  <span className="text-base font-bold text-gray-900">{formatCurrency(num(breakdownQ.data?.grand_total))}</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-amber-50 px-4 py-3">
                  <span className="text-sm text-amber-700">{t('payoutList.breakdown.unpaidTotal')}</span>
                  <span className="text-base font-bold text-amber-700">{formatCurrency(num(breakdownQ.data?.unpaid_total))}</span>
                </div>
              </div>
            )}
            {!breakdownQ.isLoading && !breakdownQ.isError && breakdownBookings.length === 0 && (
              <p className="text-sm text-gray-500 py-6 text-center">{t('payoutList.breakdown.empty')}</p>
            )}
            <div className="space-y-4 max-h-[45vh] overflow-y-auto">
              {breakdownBookings.map((b) => {
                const payableLines = (b.sessions ?? []).filter((s: any) => num(s.remaining) > 0)
                const allSelected = payableLines.length > 0 && payableLines.every((s: any) => selected.has(s.transaction_id))
                return (
                  <div key={b.booking_id} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-3">
                      <div className="min-w-0 flex items-center gap-3">
                        {payableLines.length > 0 && (
                          <input type="checkbox" checked={allSelected} onChange={e => toggleBooking(b, e.target.checked)}
                            className="w-4 h-4 accent-brand-500" title={t('payoutList.breakdown.selectAll')} />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{b.package_name || t('payoutList.breakdown.booking')} <span className="font-mono text-xs text-gray-400">#{b.booking_id}</span></p>
                          <p className="text-xs text-gray-400">{formatDate(b.booking_date)} · {b.payment_method || '—'} · {t('payoutList.breakdown.sessionsCount', { done: b.completed_sessions, total: b.no_of_sessions })}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(num(b.net_payout))}</span>
                        {num(b.unpaid_total) > 0 && <p className="text-xs text-amber-600">{t('payoutList.breakdown.unpaid')}: {formatCurrency(num(b.unpaid_total))}</p>}
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                          <th className="py-2 px-3 w-8"></th>
                          <th className="py-2 px-3">{t('payoutList.breakdown.session')}</th>
                          <th className="py-2 px-3">{t('payoutList.breakdown.statusCol')}</th>
                          <th className="py-2 px-3 text-right">{t('payoutList.breakdown.earned')}</th>
                          <th className="py-2 px-3 text-right">{t('payoutList.breakdown.remaining')}</th>
                          <th className="py-2 px-3 text-right">{t('payoutList.breakdown.payNow')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(b.sessions ?? []).map((s: any, i: number) => {
                          const rem = num(s.remaining)
                          const isSel = selected.has(s.transaction_id)
                          const partial = num(s.paid_amount) > 0 && rem > 0
                          return (
                            <tr key={s.transaction_id ?? i} className={`border-b border-gray-50 last:border-0 ${s.paid ? 'opacity-60' : ''}`}>
                              <td className="py-2 px-3">
                                {rem > 0
                                  ? <input type="checkbox" checked={isSel} onChange={() => toggleSession(s)} className="w-4 h-4 accent-brand-500" />
                                  : <span className="text-green-500" title={t('payoutList.breakdown.paidBadge')}>✓</span>}
                              </td>
                              <td className="py-2 px-3 text-gray-500">#{i + 1}{s.remarks ? ` · ${s.remarks}` : ''}<span className="block text-[11px] text-gray-400">{formatDate(s.date)}</span></td>
                              <td className="py-2 px-3">
                                {s.paid
                                  ? <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded-full bg-green-50 text-green-700 border border-green-200">{t('payoutList.breakdown.paidBadge')}{s.paid_at ? ` · ${formatDate(s.paid_at)}` : ''}</span>
                                  : partial
                                    ? <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded-full bg-blue-50 text-blue-700 border border-blue-200">{t('payoutList.breakdown.partialBadge', { paid: formatCurrency(num(s.paid_amount)) })}</span>
                                    : <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded-full bg-amber-50 text-amber-700 border border-amber-200">{t('payoutList.breakdown.unpaidBadge')}</span>}
                              </td>
                              <td className="py-2 px-3 text-right text-gray-600">{formatCurrency(num(s.amount))}</td>
                              <td className="py-2 px-3 text-right font-medium">{formatCurrency(rem)}</td>
                              <td className="py-2 px-3 text-right">
                                {rem > 0 && isSel
                                  ? <input type="number" min="0" step="0.01" max={rem} value={amounts[s.transaction_id] ?? ''}
                                      onChange={e => setAmount(s.transaction_id, e.target.value)}
                                      className="w-24 px-2 py-1 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
                                  : <span className="text-gray-300">—</span>}
                              </td>
                            </tr>
                          )
                        })}
                        {(!b.sessions || b.sessions.length === 0) && (
                          <tr><td colSpan={6} className="py-3 px-3 text-xs text-gray-400">{t('payoutList.breakdown.noSessions')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </div>

            {/* Pay selected sessions */}
            {breakdownBookings.length > 0 && (
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">{t('payoutList.pay.paymentMethod')}</span>
                    <select value={sessMethod} onChange={e => setSessMethod(e.target.value)}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">{t('payoutList.pay.txnId')}</span>
                    <input value={sessTxn} onChange={e => setSessTxn(e.target.value)} placeholder={t('payoutList.pay.txnIdPlaceholder')}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">{t('payoutList.pay.remarks')}</span>
                    <input value={sessRemarks} onChange={e => setSessRemarks(e.target.value)} placeholder={t('payoutList.pay.remarksPlaceholder')}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('payoutList.breakdown.selectedSummary', { count: selected.size, amount: formatCurrency(selectedTotal) })}</span>
                  <Button variant="primary" loading={paySessions.isPending} disabled={selected.size === 0} onClick={submitSessionPay}>
                    {t('payoutList.breakdown.paySelected')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
